/**
 * 인증 상태 관리 커스텀 훅
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import authService from '../services/authService';
import { LoginRequest, SignupRequest } from '../types/auth';
import { getErrorMessage } from '../utils/api';
import {
  setAccessToken,
  setRefreshToken,
  setUser,
  clearAuthData
} from '../utils/storage';

export const useAuth = () => {
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    setAuth,
    setLoading,
    setError,
    logout: logoutStore,
  } = useAuthStore();

  /**
   * 로그인 처리
   */
  const login = useCallback(
    async (credentials: LoginRequest) => {
      try {
        setLoading(true);
        setError(null);

        // 1. 로그인 API 호출 (토큰 획득)
        const authData = await authService.login(credentials);

        // 2. 토큰을 먼저 저장 (getCurrentUser에서 사용하기 위해)
        setAccessToken(authData.access_token);
        setRefreshToken(authData.refresh_token);

        // 3. 사용자 정보 조회 (이제 Axios 인터셉터가 토큰을 헤더에 추가함)
        const userData = await authService.getCurrentUser();

        // 4. 사용자 정보도 저장
        setUser(userData);

        // 5. Zustand 상태 업데이트
        setAuth(userData, authData);

        return userData;
      } catch (err: any) {
        const errorMessage = getErrorMessage(err);
        setError(errorMessage);
        // 로그인 실패 시 저장된 토큰 제거
        clearAuthData();
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [setAuth, setLoading, setError]
  );

  /**
   * 회원가입 처리 (그룹 관리자)
   */
  const signup = useCallback(
    async (data: SignupRequest) => {
      try {
        setLoading(true);
        setError(null);

        // 회원가입 API 호출
        const result = await authService.signup(data);

        return result;
      } catch (err: any) {
        const errorMessage = getErrorMessage(err);
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setError]
  );

  /**
   * 로그아웃 처리
   */
  const logout = useCallback(async () => {
    try {
      // 백엔드 로그아웃 API 호출 (선택적)
      await authService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // 상태 초기화
      logoutStore();
      // 로그인 페이지로 이동
      navigate('/login', { replace: true });
    }
  }, [logoutStore, navigate]);

  /**
   * 역할별 대시보드로 이동
   */
  const navigateToDashboard = useCallback(() => {
    if (!user) return;

    switch (user.role) {
      case 'admin':
        navigate('/system', { replace: true });
        break;
      case 'group_admin':
      case 'instructor':
        navigate('/dashboard', { replace: true });
        break;
      case 'student':
        navigate('/workbooks', { replace: true });
        break;
      default:
        navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  return {
    // 상태
    user,
    isAuthenticated,
    isLoading,
    error,

    // Actions
    login,
    signup,
    logout,
    navigateToDashboard,
  };
};
